import { Card, Typography, Button, Row, Col, Modal } from "antd";
import { Pie, Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from "chart.js";
import { useState, useRef } from "react";
import { ArrowLeftOutlined, WomanOutlined, ManOutlined, ExpandOutlined, DownloadOutlined } from "@ant-design/icons";
import { getDistribucionPorGenero } from "../../../services/participantes/dashboardService";
import dayjs from "dayjs";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels, CategoryScale, LinearScale, BarElement);

const { Title } = Typography;

export function DistribucionesChart({ data, filters }) {
  const pieChartRef = useRef(null);
  const barChartRef = useRef(null);
  const [view, setView] = useState('genero'); 
  const [selectedGenero, setSelectedGenero] = useState(null);
  const [detalleData, setDetalleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalView, setModalView] = useState('pie'); 
  const [modalTitle, setModalTitle] = useState('');

  const downloadChart = (chartType) => {
    let chartRef, fileName;
    if (chartType === 'pie' && pieChartRef.current) {
      chartRef = pieChartRef.current;
      fileName = `distribucion-genero-${dayjs().format('YYYY-MM-DD')}.png`;
    } else if (chartType === 'bar' && barChartRef.current) {
      chartRef = barChartRef.current;
      const generoNombre = selectedGenero === 'Femenino' ? 'mujer' : 'hombre';
      fileName = `distribucion-estado-${generoNombre}-${dayjs().format('YYYY-MM-DD')}.png`;
    } else {
      return;
    }
    const chartInstance = chartRef;
    const originalCanvas = chartInstance.canvas;
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = originalCanvas.width;
    tempCanvas.height = originalCanvas.height;
    tempCtx.fillStyle = 'white';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempCtx.drawImage(originalCanvas, 0, 0);
    const image = tempCanvas.toDataURL('image/png', 1.0);
    const link = document.createElement('a');
    link.download = fileName;
    link.href = image;
    link.click();
  };
  const openPieModal = () => {
    setModalView('pie');
    setModalTitle('Distribución por género - Vista ampliada');
    setIsModalOpen(true);
  };
  const openBarModal = () => {
    setModalView('bar');
    const generoNombre = selectedGenero === 'Femenino' ? 'Mujer' : 'Hombre';
    setModalTitle(`Distribución por estado - ${generoNombre} - Vista ampliada`);
    setIsModalOpen(true);
  };
  const cargarDetalleGenero = async (genero) => {
    setLoading(true);
    try {
      const generoParam = genero === 'Femenino' ? 'F' :
        genero === 'Masculino' ? 'M' : genero;

      const response = await getDistribucionPorGenero(
        filters,
        generoParam
      );
      setDetalleData(response.data.data);
      setSelectedGenero(genero);
      setView('detalle');
    } catch (error) {
      console.error('Error cargando datos detallados:', error);
    } finally {
      setLoading(false);
    }
  };
  if (!data || !data.sexo || data.sexo.length === 0) {
    return (
      <Card style={{ borderRadius: 12 }}>
        <Title level={5}>Distribución por género</Title>
        <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography.Text type="secondary">No hay datos para mostrar</Typography.Text>
        </div>
      </Card>
    );
  }
  const sexoData = {
    F: data.sexo.find(item => item.label === 'Femenino')?.total || 0,
    M: data.sexo.find(item => item.label === 'Masculino')?.total || 0
  };
  const totalGeneral = sexoData.F + sexoData.M;
  const colores = {
    F: '#21CFDB',
    M: '#347CC4'
  };
  const iconos = {
    F: <WomanOutlined style={{ fontSize: '20px', color: '#21CFDB' }} />,
    M: <ManOutlined style={{ fontSize: '20px', color: '#347CC4' }} />
  };
  const pieChartData = {
    labels: ['Mujer', 'Hombre'],
    datasets: [
      {
        data: [sexoData.F, sexoData.M],
        backgroundColor: [colores.F, colores.M],
        borderWidth: 2,
        borderColor: "#fff",
        hoverBorderWidth: 3,
      },
    ],
  };
  const pieBaseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      datalabels: {
        color: "#000",
        font: {
          weight: "bold",
          size: 14
        },
        formatter: (value, context) => {
          if (!value) return "";
          const porcentaje = ((value / totalGeneral) * 100).toFixed(1);
          return `${value}\n(${porcentaje}%)`;
        },
        textAlign: 'center'
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.raw;
            const percentage = ((value / totalGeneral) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const genero = index === 0 ? 'Femenino' : 'Masculino';
        cargarDetalleGenero(genero);
      }
    },
  };
  const pieModalOptions = {
    ...pieBaseOptions,
    plugins: {
      ...pieBaseOptions.plugins,
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14
          }
        }
      },
      datalabels: {
        ...pieBaseOptions.plugins.datalabels,
        font: {
          weight: "bold",
          size: 16
        }
      }
    }
  };
  const renderDetalleView = () => {
    if (!detalleData) return null;

    const estadosConfig = [
      { key: 'verificado', label: 'Verificado', color: '#20AC00' },
      { key: 'enProceso', label: 'En Proceso', color: '#2979FF' },
      { key: 'noContinua', label: 'No Continua', color: '#D62828' },
      { key: 'sinValidacion', label: 'Sin Validación', color: '#B4B4B4' }
    ];
    const datos = estadosConfig.map(estado => ({
      label: estado.label,
      value: detalleData[estado.key] || 0,
      color: estado.color,
      porcentaje: detalleData.total > 0 ? ((detalleData[estado.key] || 0) / detalleData.total * 100).toFixed(1) : 0
    }));
    const barChartData = {
      labels: datos.map(d => d.label),
      datasets: [
        {
          label: 'Cantidad',
          data: datos.map(d => d.value),
          backgroundColor: datos.map(d => d.color),
          borderColor: datos.map(d => d.color),
          borderWidth: 1,
          datalabels: {
            color: '#fff',
            font: {
              weight: "bold",
              size: 12
            },
            formatter: (value, context) => {
              if (value === 0) return '';
              const porcentaje = datos[context.dataIndex].porcentaje;
              return `${value}\n(${porcentaje}%)`;
            },
            align: 'center',
            anchor: 'center'
          }
        }
      ]
    };

    const barBaseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          callbacks: {
            label: function (context) {
              const value = context.parsed.y;
              const porcentaje = datos[context.dataIndex].porcentaje;
              return `${value} participantes (${porcentaje}%)`;
            }
          }
        },
        datalabels: {
          color: '#fff',
          font: {
            weight: 'bold',
            size: 12,
          },
          formatter: (value, context) => {
            if (value === 0) return '';
            const porcentaje = datos[context.dataIndex].porcentaje;
            return `${value}\n(${porcentaje}%)`;
          },
          anchor: 'end',
          align: 'end',
          borderRadius: 4,
          padding: { top: 4, bottom: 4, left: 6, right: 6 },
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 11 } },
          barPercentage: 0.7,
          categoryPercentage: 0.6,
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0 },
          title: {
            display: true,
            text: 'Cantidad',
            font: { size: 12, weight: 'bold' }
          }
        }
      },
      elements: {
        bar: {
          maxBarThickness: 40,
          minBarLength: 30,
        }
      }
    };

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => setView('genero')}
              style={{ marginRight: 8 }}
              type="text"
            >
              Volver
            </Button>
            <Title level={5} style={{ margin: 0 }}>
              {selectedGenero === 'Femenino' ? iconos.F : iconos.M}
              Distribución por estado - {detalleData.genero || (selectedGenero === 'Femenino' ? 'Mujer' : 'Hombre')}
            </Title>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              icon={<ExpandOutlined />}
              size="small"
              onClick={openBarModal}
              title="Maximizar gráfica"
            />
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => downloadChart('bar')}
              title="Descargar como imagen"
            />
          </div>
        </div>
        <div style={{ height: 350 }}>
          <Bar
            ref={barChartRef}
            data={barChartData}
            options={barBaseOptions}
            plugins={[ChartDataLabels]}
          />
        </div>
      </>
    );
  };

  const renderGeneroView = () => {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={5} style={{ margin: 0 }}>Distribución por género</Title>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              icon={<ExpandOutlined />}
              size="small"
              onClick={openPieModal}
              title="Maximizar gráfica"
            />
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => downloadChart('pie')}
              title="Descargar como imagen"
            />
          </div>
        </div>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Card
              hoverable
              onClick={() => cargarDetalleGenero('Femenino')}
              style={{
                borderLeft: `4px solid ${colores.F}`,
                minHeight: 40,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: "0px 16px",
                cursor: "pointer"
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', fontSize: 16, fontWeight: 600 }}>
                {iconos.F}
                <span>
                  Mujer {sexoData.F} ({totalGeneral > 0 ? ((sexoData.F / totalGeneral) * 100).toFixed(1) : 0}%)
                </span>
              </div>
              <div>
                Click para ver detalles
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card
              hoverable
              onClick={() => cargarDetalleGenero('Masculino')}
              style={{
                borderLeft: `4px solid ${colores.M}`,
                minHeight: 40,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: "0px 16px",
                cursor: "pointer"
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', fontSize: 16, fontWeight: 600 }}>
                {iconos.M}
                <span>
                  Hombre {sexoData.M} ({totalGeneral > 0 ? ((sexoData.M / totalGeneral) * 100).toFixed(1) : 0}%)
                </span>
              </div>
              <div>
                Click para ver detalles
              </div>
            </Card>
          </Col>
        </Row>
        <div style={{ height: 250 }}>
          <Pie
            ref={pieChartRef}
            data={pieChartData}
            options={pieBaseOptions}
            plugins={[ChartDataLabels]}
          />
        </div>
      </>
    );
  };

  const renderModalBarContent = () => {
    if (!detalleData) return null;

    const estadosConfig = [
      { key: 'verificado', label: 'Verificado', color: '#20AC00' },
      { key: 'enProceso', label: 'En Proceso', color: '#2979FF' },
      { key: 'noContinua', label: 'No Continua', color: '#D62828' },
      { key: 'sinValidacion', label: 'Sin Validación', color: '#B4B4B4' }
    ];

    const datos = estadosConfig.map(estado => ({
      label: estado.label,
      value: detalleData[estado.key] || 0,
      color: estado.color,
      porcentaje: detalleData.total > 0 ? ((detalleData[estado.key] || 0) / detalleData.total * 100).toFixed(1) : 0
    }));

    const barChartData = {
      labels: datos.map(d => d.label),
      datasets: [
        {
          label: 'Cantidad',
          data: datos.map(d => d.value),
          backgroundColor: datos.map(d => d.color),
          borderColor: datos.map(d => d.color),
          borderWidth: 1,
          datalabels: {
            color: '#fff',
            font: {
              weight: "bold",
              size: 14
            },
            formatter: (value, context) => {
              if (value === 0) return '';
              const porcentaje = datos[context.dataIndex].porcentaje;
              return `${value}\n(${porcentaje}%)`;
            },
            align: 'center',
            anchor: 'center'
          }
        }
      ]
    };

    const barModalOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#2979FF',
          borderWidth: 1,
          cornerRadius: 6,
          padding: 12,
          callbacks: {
            label: function (context) {
              const value = context.parsed.y;
              const porcentaje = datos[context.dataIndex].porcentaje;
              return `${value} participantes (${porcentaje}%)`;
            }
          }
        },
        datalabels: {
          color: '#fff',
          font: {
            weight: 'bold',
            size: 14,
          },
          formatter: (value, context) => {
            if (value === 0) return '';
            const porcentaje = datos[context.dataIndex].porcentaje;
            return `${value}\n(${porcentaje}%)`;
          },
          anchor: 'end',
          align: 'end',
          borderRadius: 4,
          padding: { top: 6, bottom: 6, left: 8, right: 8 },
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 13 } },
          barPercentage: 0.8,
          categoryPercentage: 0.7,
        },
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0,
            font: { size: 12 }
          },
          title: {
            display: true,
            text: 'Cantidad',
            font: { size: 14, weight: 'bold' }
          }
        }
      },
      elements: {
        bar: {
          maxBarThickness: 50,
          minBarLength: 40,
        }
      }
    };

    return (
      <>
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>
            {selectedGenero === 'Femenino' ? iconos.F : iconos.M}
            Distribución por estado - {detalleData.genero || (selectedGenero === 'Femenino' ? 'Mujer' : 'Hombre')}
          </Title>
        </div>
        <Bar
          data={barChartData}
          options={barModalOptions}
          plugins={[ChartDataLabels]}
        />
      </>
    );
  };

  return (
    <>
      <Card
        style={{
          borderRadius: 12,
          minHeight: 500
        }}
        loading={loading}
      >
        {view === 'genero' ? renderGeneroView() : renderDetalleView()}
      </Card>
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{modalTitle}</span>
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => downloadChart(modalView)}
            >
              Descargar
            </Button>
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width="90vw"
        style={{ maxWidth: 1200 }}
      >
        <div style={{
          height: '70vh',
          minHeight: 500,
          background: 'white',
          padding: 24
        }}>
          {modalView === 'pie' ? (
            <Pie
              data={pieChartData}
              options={pieModalOptions}
              plugins={[ChartDataLabels]}
            />
          ) : (
            renderModalBarContent()
          )}
        </div>
      </Modal>
    </>
  );
}